// src/components/classroom/RosterPanel.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  Users, 
  Mic, 
  MicOff, 
  Camera, 
  CameraOff, 
  Hand,
  UserX,
  RotateCcw,
  MessageSquare
} from 'lucide-react';
import { useClassroom } from '../../contexts/ClassroomContext';

const RosterContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #2a2a2a;
  color: white;
`;

const RosterHeader = styled.div`
  padding: 15px;
  border-bottom: 1px solid #444;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ControlBar = styled.div`
  padding: 10px 15px;
  background: #333;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const ControlButton = styled.button`
  padding: 8px 12px;
  background: ${props => props.active ? '#4CAF50' : '#555'};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  
  &:hover {
    background: ${props => props.active ? '#45a049' : '#666'};
  }
`;

const StudentList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 10px;
`;

const StudentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
  margin-bottom: 5px;
  background: #333;
  border-radius: 8px;
  cursor: pointer;
  
  &:hover {
    background: #404040;
  }
`;

const StudentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StudentName = styled.span`
  font-weight: 500;
`;

const StatusIndicators = styled.div`
  display: flex;
  gap: 5px;
`;

const StatusIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => {
    if (props.type === 'auth' && props.active) return '#4CAF50';
    if (props.type === 'mic' && props.active) return '#2196F3';
    if (props.type === 'camera' && props.active) return '#FF9800';
    if (props.type === 'hand' && props.active) return '#FFC107';
    return '#666';
  }};
`;

const StudentActions = styled.div`
  display: flex;
  gap: 5px;
`;

const ActionButton = styled.button`
  padding: 5px;
  background: #555;
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  
  &:hover {
    background: #666;
  }
`;

const RosterPanel = () => {
  const { students, updateStudentStatus, removeStudent } = useClassroom();
  const [shiftMode, setShiftMode] = useState(false);

  const handleStudentAction = async (studentId, action) => {
    switch (action) {
      case 'toggleAuth':
        // Toggle authorization
        break;
      case 'toggleMic':
        // Toggle microphone
        break;
      case 'toggleCamera':
        // Toggle camera
        break;
      case 'remove':
        await removeStudent(studentId);
        break;
      default:
        break;
    }
  };

  return (
    <RosterContainer>
      <RosterHeader>
        <Users size={20} />
        <span>Roster</span>
        <span style={{ marginLeft: 'auto' }}>
          {students.length} students
        </span>
      </RosterHeader>
      
      <ControlBar>
        <ControlButton 
          active={shiftMode}
          onClick={() => setShiftMode(!shiftMode)}
        >
          <RotateCcw size={14} />
          Shift
        </ControlButton>
        
        <ControlButton>
          <MessageSquare size={14} />
          Chat
        </ControlButton>
      </ControlBar>
      
      <StudentList>
        {students.map(student => (
          <StudentItem key={student.id}>
            <StudentInfo>
              <StudentName>{student.name}</StudentName>
              <StatusIndicators>
                <StatusIcon type="auth" active={student.authorized}>
                  {student.authorized ? <Hand size={12} /> : <UserX size={12} />}
                </StatusIcon>
                <StatusIcon type="mic" active={student.micEnabled}>
                  {student.micEnabled ? <Mic size={12} /> : <MicOff size={12} />}
                </StatusIcon>
                <StatusIcon type="camera" active={student.cameraEnabled}>
                  {student.cameraEnabled ? <Camera size={12} /> : <CameraOff size={12} />}
                </StatusIcon>
                {student.handRaised && (
                  <StatusIcon type="hand" active={true}>
                    <Hand size={12} />
                  </StatusIcon>
                )}
              </StatusIndicators>
            </StudentInfo>
            
            <StudentActions>
              <ActionButton 
                onClick={() => handleStudentAction(student.id, 'remove')}
                title="Remove student"
              >
                <UserX size={14} />
              </ActionButton>
            </StudentActions>
          </StudentItem>
        ))}
      </StudentList>
    </RosterContainer>
  );
};

export default RosterPanel;